import type { FormEvent } from "react"

import { Button, focusOnMount, Input, Label, Skeleton } from "@coval/ui"

import type { ClerkSigninPhase } from "./clerkSigninState"

interface SigninErrorProps {
  error: string | null
}

export function SigninError({ error }: SigninErrorProps) {
  return error ? (
    <p
      id="signin-error"
      role="alert"
      className="rounded-md border border-coval-danger-border bg-coval-danger-surface p-3 text-sm text-coval-danger-on-surface"
    >
      {error}
    </p>
  ) : null
}

interface ClerkInvitationSigninStatusProps {
  error: string | null
  pending: boolean
  ticketPending: boolean
  onRetry: () => void
}

export function ClerkInvitationSigninStatus({
  error,
  pending,
  ticketPending,
  onRetry,
}: ClerkInvitationSigninStatusProps) {
  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-xl font-medium text-coval-text text-center md:text-left">
          Accepting invitation
        </h1>
        <p className="text-sm text-coval-text-muted">
          {error ? "Invitation sign-in failed." : "Signing you in…"}
        </p>
      </div>

      {pending && <Skeleton className="h-9 w-full rounded-md" />}

      {error && (
        <div className="space-y-3">
          <SigninError error={error} />
          <Button
            type="button"
            className="w-full"
            disabled={ticketPending}
            aria-label="Retry invitation sign-in"
            onClick={onRetry}
          >
            Try again
          </Button>
        </div>
      )}
    </div>
  )
}

interface EmailCodeSigninFormProps {
  phase: Extract<ClerkSigninPhase, { kind: "email_code" }>
  code: string
  error: string | null
  submitting: boolean
  onCodeChange: (code: string) => void
  onResend: () => void
  onReset: () => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
}

export function EmailCodeSigninForm({
  phase,
  code,
  error,
  submitting,
  onCodeChange,
  onResend,
  onReset,
  onSubmit,
}: EmailCodeSigninFormProps) {
  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-xl font-medium text-coval-text text-center md:text-left">
          Check your email
        </h1>
        <p className="text-sm text-coval-text-muted">
          We sent a 6-digit code to{" "}
          <strong data-testid="signin-email-code-target">
            {phase.safeIdentifier}
          </strong>
          .
        </p>
      </div>

      <form
        onSubmit={onSubmit}
        className="space-y-6"
        aria-describedby={error ? "signin-error" : undefined}
      >
        <div className="space-y-1">
          <Label className="font-medium" htmlFor="signin-code">
            Verification code
          </Label>
          <Input
            id="signin-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            value={code}
            onChange={(event) => onCodeChange(event.target.value.trim())}
            required
            aria-label="Verification code"
            data-testid="signin-code-input"
          />
        </div>

        <SigninError error={error} />

        <Button
          type="submit"
          className="w-full"
          disabled={submitting || code.length === 0}
          aria-label="Verify code"
          data-testid="signin-verify-code"
        >
          {submitting ? "Verifying…" : "Verify"}
        </Button>

        <div className="flex items-center justify-between text-sm">
          <button
            type="button"
            className="text-coval-text-muted underline hover:text-coval-text disabled:opacity-50"
            onClick={onResend}
            disabled={submitting}
            data-testid="signin-resend-code"
          >
            Resend code
          </button>
          <button
            type="button"
            className="text-coval-text-muted underline hover:text-coval-text"
            onClick={onReset}
            data-testid="signin-use-different-email"
          >
            Use a different email
          </button>
        </div>
      </form>
    </div>
  )
}

interface PasswordSigninFormProps {
  phase: Extract<ClerkSigninPhase, { kind: "password" }>
  email: string
  password: string
  error: string | null
  submitting: boolean
  onPasswordChange: (password: string) => void
  onReset: () => void
  onStartEmailCode: (emailAddressId: string, safeIdentifier: string) => void
  onStartPasswordReset: (emailAddressId: string, safeIdentifier: string) => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
}

export function PasswordSigninForm({
  phase,
  email,
  password,
  error,
  submitting,
  onPasswordChange,
  onReset,
  onStartEmailCode,
  onStartPasswordReset,
  onSubmit,
}: PasswordSigninFormProps) {
  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-xl font-medium text-coval-text text-center md:text-left">
          Enter your password
        </h1>
        <p className="text-sm text-coval-text-muted">
          Signing in as <strong>{email}</strong>.
        </p>
      </div>

      <form
        onSubmit={onSubmit}
        className="space-y-6"
        aria-describedby={error ? "signin-error" : undefined}
      >
        <div className="space-y-1">
          <Label className="font-medium" htmlFor="signin-password">
            Password
          </Label>
          <Input
            id="signin-password"
            name="current-password"
            type="password"
            autoComplete="current-password"
            ref={focusOnMount}
            placeholder="Your password"
            value={password}
            onChange={(event) => onPasswordChange(event.target.value)}
            required
            aria-label="Password"
            data-testid="signin-password-input"
          />
        </div>

        <SigninError error={error} />

        <Button
          type="submit"
          className="w-full"
          disabled={submitting || password.length === 0}
          aria-label="Sign in"
        >
          {submitting ? "Signing in…" : "Sign in"}
        </Button>

        <div className="flex flex-col gap-2 text-sm">
          {phase.emailCode && (
            <button
              type="button"
              className="text-left text-coval-text-muted underline hover:text-coval-text disabled:opacity-50"
              onClick={() =>
                onStartEmailCode(
                  phase.emailCode!.emailAddressId,
                  phase.emailCode!.safeIdentifier,
                )
              }
              disabled={submitting}
              data-testid="signin-use-email-code"
            >
              Sign in with a code instead
            </button>
          )}
          {phase.resetPassword && (
            <button
              type="button"
              className="text-left text-coval-text-muted underline hover:text-coval-text disabled:opacity-50"
              onClick={() =>
                onStartPasswordReset(
                  phase.resetPassword!.emailAddressId,
                  phase.resetPassword!.safeIdentifier,
                )
              }
              disabled={submitting}
              data-testid="signin-forgot-password"
            >
              Forgot password?
            </button>
          )}
          <button
            type="button"
            className="text-left text-coval-text-muted underline hover:text-coval-text disabled:opacity-50"
            onClick={onReset}
            disabled={submitting}
            data-testid="signin-use-different-email"
          >
            Use a different email
          </button>
        </div>
      </form>
    </div>
  )
}

interface ResetPasswordSigninFormProps {
  phase: Extract<ClerkSigninPhase, { kind: "reset_password" }>
  code: string
  password: string
  error: string | null
  submitting: boolean
  onCodeChange: (code: string) => void
  onPasswordChange: (password: string) => void
  onResend: () => void
  onReset: () => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
}

export function ResetPasswordSigninForm({
  phase,
  code,
  password,
  error,
  submitting,
  onCodeChange,
  onPasswordChange,
  onResend,
  onReset,
  onSubmit,
}: ResetPasswordSigninFormProps) {
  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-xl font-medium text-coval-text text-center md:text-left">
          Reset your password
        </h1>
        <p className="text-sm text-coval-text-muted">
          We sent a 6-digit code to{" "}
          <strong data-testid="signin-reset-target">
            {phase.safeIdentifier}
          </strong>
          . Enter it with a new password.
        </p>
      </div>

      <form
        onSubmit={onSubmit}
        className="space-y-6"
        aria-describedby={error ? "signin-error" : undefined}
      >
        <div className="space-y-1">
          <Label className="font-medium" htmlFor="signin-reset-code">
            Verification code
          </Label>
          <Input
            id="signin-reset-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            value={code}
            onChange={(event) => onCodeChange(event.target.value.trim())}
            required
            aria-label="Verification code"
            data-testid="signin-reset-code-input"
          />
        </div>

        <div className="space-y-1">
          <Label className="font-medium" htmlFor="signin-reset-password">
            New password
          </Label>
          <Input
            id="signin-reset-password"
            type="password"
            autoComplete="new-password"
            placeholder="New password"
            value={password}
            onChange={(event) => onPasswordChange(event.target.value)}
            required
            aria-label="New password"
            data-testid="signin-reset-password-input"
          />
        </div>

        <SigninError error={error} />

        <Button
          type="submit"
          className="w-full"
          disabled={submitting || code.length === 0 || password.length === 0}
          aria-label="Reset password and sign in"
          data-testid="signin-reset-submit"
        >
          {submitting ? "Resetting…" : "Reset password"}
        </Button>

        <div className="flex items-center justify-between text-sm">
          <button
            type="button"
            className="text-coval-text-muted underline hover:text-coval-text disabled:opacity-50"
            onClick={onResend}
            disabled={submitting}
            data-testid="signin-reset-resend-code"
          >
            Resend code
          </button>
          <button
            type="button"
            className="text-coval-text-muted underline hover:text-coval-text disabled:opacity-50"
            onClick={onReset}
            disabled={submitting}
            data-testid="signin-use-different-email"
          >
            Use a different email
          </button>
        </div>
      </form>
    </div>
  )
}

interface SecondFactorSigninFormProps {
  phase: Extract<ClerkSigninPhase, { kind: "second_factor" }>
  code: string
  error: string | null
  submitting: boolean
  onCodeChange: (code: string) => void
  onToggleMode: () => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
}

export function SecondFactorSigninForm({
  phase,
  code,
  error,
  submitting,
  onCodeChange,
  onToggleMode,
  onSubmit,
}: SecondFactorSigninFormProps) {
  const label =
    phase.mode === "totp"
      ? "Enter the 6-digit code from your authenticator app."
      : "Enter a backup code."
  const inputLabel =
    phase.mode === "totp" ? "Authenticator code" : "Backup code"

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-xl font-medium text-coval-text text-center md:text-left">
          Two-step verification
        </h1>
        <p className="text-sm text-coval-text-muted">{label}</p>
      </div>

      <form
        onSubmit={onSubmit}
        className="space-y-6"
        aria-describedby={error ? "signin-error" : undefined}
      >
        <div className="space-y-1">
          <Label className="font-medium" htmlFor="signin-mfa-code">
            {inputLabel}
          </Label>
          <Input
            id="signin-mfa-code"
            inputMode={phase.mode === "totp" ? "numeric" : "text"}
            autoComplete="one-time-code"
            value={code}
            onChange={(event) => onCodeChange(event.target.value)}
            required
            aria-label={inputLabel}
            data-testid="signin-mfa-input"
          />
        </div>

        <SigninError error={error} />

        <Button
          type="submit"
          className="w-full"
          disabled={submitting || code.length === 0}
          aria-label="Verify"
          data-testid="signin-mfa-submit"
        >
          {submitting ? "Verifying…" : "Verify"}
        </Button>

        {phase.hasBackup && (
          <button
            type="button"
            className="text-sm text-coval-text-muted underline hover:text-coval-text"
            onClick={onToggleMode}
            data-testid="signin-mfa-toggle"
          >
            {phase.mode === "totp"
              ? "Use a backup code instead"
              : "Use authenticator app instead"}
          </button>
        )}
      </form>
    </div>
  )
}
